"use client";

import { BookHeart, Bot, Clapperboard, Film, Gamepad2, HeartHandshake, House, LayoutGrid, Menu } from "lucide-react";
import { ESCORTS_URL } from "@/config/escorts";
import { gameUrl } from "@/config/game";
import Link from "@/components/ui/Link";
import { Suspense, useEffect, useRef, useState } from "react";
import { useCoinsWaiting } from "@/lib/coins-badge";
import { cn } from "@/lib/cn";
import { MODES, modeOf } from "./modes";
import { isActive } from "./nav";
import { useShell } from "./ShellProvider";
import { useSitePath } from "@/components/ui/use-site-path";
import { useLang } from "@/components/ui/lang-context";
import { useUi } from "@/components/ui/use-ui";

// Streams, Videos and Shorts, and the sections behind one button (VR, Cartoon, Hentai, the Game), like the menu's
// switch (Sidebar.tsx); Saved is in the menu, and search in each page's header.
const LINKS: { href: string; key: "home" | "videos" | "shorts"; icon: typeof House }[] = [
  { href: "/", key: "home", icon: House },
  { href: "/videos", key: "videos", icon: Film },
  { href: "/shorts", key: "shorts", icon: Clapperboard },
];
const SECTION_MODES = MODES.filter((m) => m.id !== "streams" && m.id !== "videos" && m.id !== "shorts");

export function MobileNav() {
  return (
    <Suspense fallback={<MobileNavView pathname={null} />}>
      <ActiveMobileNav />
    </Suspense>
  );
}

function ActiveMobileNav() {
  return <MobileNavView pathname={useSitePath()} />;
}

function MobileNavView({ pathname }: { pathname: string | null }) {
  const { setDrawerOpen } = useShell();
  const coinsReady = useCoinsWaiting() > 0;
  const t = useUi();
  const item = "flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-[10px] transition-colors";
  return (
    <nav
      aria-label={t.nav.quickNavigation}
      className="glass fixed inset-x-2 bottom-[max(0.5rem,env(safe-area-inset-bottom))] z-30 flex h-14 items-stretch rounded-2xl px-1 shadow-[0_10px_40px_-10px_rgb(0_0_0/0.8)] md:hidden"
    >
      {LINKS.map(({ href, key, icon: Icon }) => (
        <Link key={href} href={href} className={cn(item, isActive(pathname, href) ? "text-fg" : "text-muted")}>
          <Icon size={22} strokeWidth={1.6} className={cn(isActive(pathname, href) && "text-brand-soft")} />
          {t.nav[key]}
        </Link>
      ))}
      <Sections pathname={pathname} className={item} />
      <button type="button" onClick={() => setDrawerOpen(true)} className={cn(item, "text-muted")}>
        <span className="relative">
          <Menu size={22} strokeWidth={1.6} />
          {/* Today's Sweet Coins are waiting in the menu. */}
          {coinsReady && <span aria-hidden className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-[#ff5fa2] ring-2 ring-ink" />}
        </span>
        {t.nav.menu}
        {coinsReady && <span className="sr-only">, {t.nav.coinsReady}</span>}
      </button>
    </nav>
  );
}

/** The sections' button: the current section's icon and name (or "More"), opening them and the Game above the bar. */
function Sections({ pathname, className }: { pathname: string | null; className: string }) {
  const t = useUi();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const mode = modeOf(pathname);
  const current = SECTION_MODES.find((m) => m.id === mode);
  const Icon = current?.icon ?? LayoutGrid;

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener("pointerdown", away);
    return () => window.removeEventListener("pointerdown", away);
  }, [open]);

  return (
    <div ref={box} className="relative flex flex-1">
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={cn(className, current || open ? "text-fg" : "text-muted")}>
        <Icon size={22} strokeWidth={1.6} className={cn(current && "text-brand-soft")} />
        {current ? t.nav[current.id] : t.nav.sections}
      </button>
      {open && (
        <ul className="glass absolute bottom-[calc(100%+10px)] left-1/2 w-44 -translate-x-1/2 rounded-2xl p-1.5 shadow-[0_10px_40px_-10px_rgb(0_0_0/0.8)]">
          {SECTION_MODES.map(({ id, href, icon: ModeIcon }) => (
            <li key={id}>
              <Link
                href={href}
                onClick={() => setOpen(false)}
                className={cn("flex h-11 items-center gap-3 rounded-xl px-3 text-[14px]", id === mode ? "bg-white/10 text-fg" : "text-soft hover:bg-white/[0.06]")}
              >
                <ModeIcon size={19} strokeWidth={1.7} className={cn(id === mode && "text-brand-soft")} />
                {t.nav[id]}
              </Link>
            </li>
          ))}
          <li>
            <Link
              href="/stories"
              data-stat="stories:menu"
              onClick={() => setOpen(false)}
              className="flex h-11 items-center gap-3 rounded-xl px-3 text-[14px] text-soft hover:bg-white/[0.06]"
            >
              <BookHeart size={19} strokeWidth={1.7} />
              {t.nav.stories}
            </Link>
          </li>
          <li>
            <Link href="/ai" onClick={() => setOpen(false)} className="flex h-11 items-center gap-3 rounded-xl px-3 text-[14px] text-soft hover:bg-white/[0.06]">
              <Bot size={19} strokeWidth={1.7} />
              {t.nav.ai}
            </Link>
          </li>
          <li>
            <a
              href={gameUrl(lang)}
              target="_blank"
              rel="noopener"
              data-stat="game:menu"
              onClick={() => setOpen(false)}
              className="flex h-11 items-center gap-3 rounded-xl px-3 text-[14px] text-soft hover:bg-white/[0.06]"
            >
              <Gamepad2 size={19} strokeWidth={1.7} />
              {t.nav.game}
            </a>
          </li>
          <li>
            <a
              href={ESCORTS_URL}
              target="_blank"
              rel="noopener"
              data-stat="escorts:menu"
              onClick={() => setOpen(false)}
              className="flex h-11 items-center gap-3 rounded-xl px-3 text-[14px] text-soft hover:bg-white/[0.06]"
            >
              <HeartHandshake size={19} strokeWidth={1.7} />
              {t.nav.escorts}
            </a>
          </li>
        </ul>
      )}
    </div>
  );
}
